import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fire, Lightning, Siren, ShareNetwork, MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const MONTH_NAME = { "04": "Apr", "05": "May", "06": "Jun", "07": "Jul", "08": "Aug", "09": "Sep", 10: "Oct" };

const fmtDay = (iso) =>
  iso ? new Date(iso + "T12:00:00").toLocaleDateString([], { month: "long", day: "numeric" }) : "";

function Stat({ value, label, color = "#F2542D", testid }) {
  return (
    <div className="border border-tac-border bg-tac-surface/40 px-4 py-5 text-center" data-testid={testid}>
      <div className="text-3xl sm:text-4xl font-bold tabular" style={{ color }}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-tac-faint font-bold mt-1">{label}</div>
    </div>
  );
}

export default function SeasonRecapPage() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/season-recap").then((r) => setD(r.data)).catch(() => {});
  }, []);

  const share = async () => {
    const url = `${window.location.origin}/api/share/season-recap`;
    const text = d
      ? `🔥 ${d.year} Fire Season in Review — ${d.wildfires} wildfires, ${d.acres_burned.toLocaleString()} acres, ${d.zones_issued} evacuation zones, ${d.lightning_strikes.toLocaleString()} lightning strikes. See the full recap:`
      : "Fire season recap";
    try {
      if (navigator.share) await navigator.share({ title: "Fire Season Recap", text, url });
      else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Recap copied to clipboard");
      }
    } catch { /* user cancelled */ }
  };

  if (!d) return <div className="p-10 text-tac-dim font-mono-tac" data-testid="recap-loading">Building the season recap…</div>;

  const maxM = Math.max(1, ...d.monthly.map((m) => m.count));

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8" data-testid="season-recap-page">
      <header>
        <div className="text-[12px] uppercase tracking-[0.3em] text-tac-primary font-bold">Okanogan Highlands Firewatch</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-tac-text mt-2">
          {d.year} Fire Season <span className="text-tac-primary">in Review</span>
        </h1>
        <p className="text-tac-dim mt-3 text-base max-w-xl">
          What our community watched, tracked and got through together this season —
          across {d.counties.length} Washington counties.
        </p>
        <div className="flex gap-3 mt-4">
          <button onClick={share} data-testid="recap-share-btn"
            className="inline-flex items-center gap-2 bg-tac-primary text-white font-bold px-5 py-2.5 text-sm uppercase tracking-wider">
            <ShareNetwork size={15} weight="bold" /> Share the Recap
          </button>
          <Link to="/" data-testid="recap-map-link"
            className="inline-flex items-center gap-2 border border-tac-border text-tac-text font-bold px-5 py-2.5 text-sm uppercase tracking-wider">
            <MapTrifold size={15} weight="bold" /> Live Map
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat value={d.wildfires.toLocaleString()} label="Wildfires tracked" testid="recap-wildfires" />
        <Stat value={d.acres_burned.toLocaleString()} label="Acres burned" testid="recap-acres" />
        <Stat value={d.zones_issued.toLocaleString()} label="Evac zones issued" color="#E63946" testid="recap-zones" />
        <Stat value={d.lightning_strikes.toLocaleString()} label="Lightning strikes" color="#FFD60A" testid="recap-lightning" />
      </div>

      <section className="border border-tac-border bg-tac-surface/40 p-5" data-testid="recap-top-fires">
        <h2 className="text-lg font-bold text-tac-text flex items-center gap-2 mb-3">
          <Fire size={18} weight="duotone" className="text-tac-primary" /> The Season's Biggest Fires
        </h2>
        <ol className="space-y-2">
          {d.top_fires.map((f, i) => (
            <li key={f.id} className="flex items-baseline gap-3">
              <span className="text-2xl font-bold text-tac-faint tabular w-7">{i + 1}</span>
              <div className="flex-1">
                <Link to={`/incident/${f.id}`} className="text-tac-text font-bold underline decoration-tac-primary/50">
                  {f.name}
                </Link>
                <span className="text-tac-faint text-sm"> — {f.county} Co. · started {fmtDay(f.started)}</span>
              </div>
              <span className="font-mono-tac text-tac-primary font-bold tabular">{f.acres.toLocaleString()} ac</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="border border-tac-border bg-tac-surface/40 p-5" data-testid="recap-monthly">
        <h2 className="text-lg font-bold text-tac-text mb-3">New Fire Starts by Month</h2>
        <div className="flex items-end gap-2">
          {d.monthly.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[11px] font-mono-tac text-tac-dim">{m.count}</span>
              <div
                className="w-full bg-tac-primary/80"
                style={{ height: Math.max(Math.round((m.count / maxM) * 96), m.count ? 4 : 1) }}
              />
              <span className="text-[10px] uppercase text-tac-faint font-bold">{MONTH_NAME[m.month]}</span>
            </div>
          ))}
        </div>
        {d.busiest_day && (
          <p className="text-sm text-tac-dim mt-3">
            Busiest day: <b className="text-tac-text">{fmtDay(d.busiest_day.date)}</b> — {d.busiest_day.count} new fire starts in 24 hours.
          </p>
        )}
      </section>

      <div className="grid sm:grid-cols-2 gap-3">
        <section className="border border-tac-border bg-tac-surface/40 p-5" data-testid="recap-evac">
          <h2 className="text-base font-bold text-tac-text flex items-center gap-2 mb-2">
            <Siren size={16} weight="duotone" className="text-tac-danger" /> Evacuations
          </h2>
          <p className="text-sm text-tac-dim leading-relaxed">
            <b className="text-tac-text">{d.zones_issued}</b> zones issued this season, including{" "}
            <b className="text-tac-danger">{d.zones_level3} Level 3 (GO NOW)</b> orders — every one tracked,
            mapped and pushed to members in real time.
          </p>
        </section>
        <section className="border border-tac-border bg-tac-surface/40 p-5" data-testid="recap-storms">
          <h2 className="text-base font-bold text-tac-text flex items-center gap-2 mb-2">
            <Lightning size={16} weight="duotone" className="text-[#FFD60A]" /> Storms
          </h2>
          <p className="text-sm text-tac-dim leading-relaxed">
            <b className="text-tac-text">{d.storm_days}</b> storm days brought{" "}
            <b className="text-tac-text">{d.lightning_strikes.toLocaleString()}</b> strikes
            {d.biggest_storm && <> — the biggest on <b className="text-tac-text">{fmtDay(d.biggest_storm.date)}</b> with {d.biggest_storm.strikes.toLocaleString()} strikes</>}.
          </p>
        </section>
      </div>

      <footer className="border border-tac-primary/40 bg-tac-primary/10 p-5 text-center" data-testid="recap-cta">
        <p className="text-tac-text font-bold">
          {d.members} members strong — and growing before next season.
        </p>
        <p className="text-sm text-tac-dim mt-1">
          Free push alerts, saved locations and the weekly digest keep our community ahead of the fire.
        </p>
        <Link to="/signup" className="inline-block mt-3 bg-tac-primary text-white font-bold px-6 py-2.5 text-sm uppercase tracking-wider" data-testid="recap-join-btn">
          Join Firewatch — Free
        </Link>
      </footer>
    </div>
  );
}
